import React, { useState } from 'react';
import { Form as FinalForm } from 'react-final-form';
import { useSelector } from 'react-redux';

import supportConfig from '../../config/supportConfig.json';
import { sendContactSubmission } from '../../util/api';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { emailFormatValid, maxLength, required } from '../../util/validators';
import {
  FieldSelect,
  FieldTextInput,
  Form,
  LayoutSingleColumn,
  NamedLink,
  Page,
  PrimaryButton,
} from '../../components';

import FooterContainer from '../FooterContainer/FooterContainer';
import TopbarContainer from '../TopbarContainer/TopbarContainer';

import css from './ContactPage.module.css';

/**
 * General PageHop support contact page.
 *
 * @component
 * @returns {JSX.Element}
 */
const ContactPage = props => {
  const intl = useIntl();
  const currentUser = useSelector(state => state.user?.currentUser);
  const [submissionState, setSubmissionState] = useState('idle');
  const currentName =
    currentUser?.attributes?.profile?.displayName ||
    [currentUser?.attributes?.profile?.firstName, currentUser?.attributes?.profile?.lastName]
      .filter(Boolean)
      .join(' ') ||
    '';
  const currentEmail = currentUser?.attributes?.email || '';
  const { limits, contactCategories } = supportConfig;

  const requiredMessage = intl.formatMessage({ id: 'ContactPage.required' });
  const validEmailMessage = intl.formatMessage({ id: 'ContactPage.emailInvalid' });
  const tooLong = max => intl.formatMessage({ id: 'ContactPage.tooLong' }, { max });

  const handleSubmit = async values => {
    setSubmissionState('submitting');
    const isTechnicalIssue = values.category === 'technical';
    const pageUrl =
      typeof window !== 'undefined'
        ? window.location.href
        : `${props.location?.pathname || '/contact'}`;
    const browser = typeof navigator !== 'undefined' ? navigator.userAgent : '';
    const isMobile =
      typeof window !== 'undefined' &&
      (navigator.userAgentData?.mobile ||
        (typeof window.matchMedia === 'function'
          ? window.matchMedia('(max-width: 767px)').matches
          : window.innerWidth < 768));
    try {
      await sendContactSubmission({
        category: values.category,
        subject: values.subject,
        message: values.message,
        name: values.name,
        email: values.email,
        where: values.where,
        company: values.company,
        path: props.location?.pathname || '/contact',
        ...(isTechnicalIssue
          ? { pageUrl, browser, deviceType: isMobile ? 'mobile' : 'desktop' }
          : {}),
      });
      setSubmissionState('success');
    } catch (error) {
      setSubmissionState('error');
    }
  };

  return (
    <Page
      title={intl.formatMessage({ id: 'ContactPage.schemaTitle' })}
      description={intl.formatMessage({ id: 'ContactPage.description' })}
    >
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
        <main className={css.root} id="main-content">
          <div className={css.content}>
            <header className={css.header}>
              <p className={css.eyebrow}>
                <FormattedMessage id="ContactPage.eyebrow" />
              </p>
              <h1 className={css.heading}>
                <FormattedMessage id="ContactPage.heading" />
              </h1>
              <p className={css.intro}>
                <FormattedMessage id="ContactPage.intro" />
              </p>
              <p className={css.reportNote}>
                <FormattedMessage id="ContactPage.reportNote" />{' '}
                <NamedLink name="ReportPage">
                  <FormattedMessage id="ContactPage.reportLink" />
                </NamedLink>
              </p>
            </header>

            <FinalForm
              initialValues={{ name: currentName, email: currentEmail }}
              onSubmit={handleSubmit}
              render={({ handleSubmit: submitForm, invalid }) => (
                <Form className={css.form} onSubmit={submitForm}>
                  <FieldTextInput
                    className={css.honeypot}
                    id="contact-company"
                    name="company"
                    type="text"
                    tabIndex="-1"
                    autoComplete="off"
                    aria-hidden="true"
                  />
                  <FieldSelect
                    id="contact-category"
                    name="category"
                    label={intl.formatMessage({ id: 'ContactPage.categoryLabel' })}
                    required
                    validate={required(requiredMessage)}
                  >
                    <option value="">
                      {intl.formatMessage({ id: 'ContactPage.categoryPlaceholder' })}
                    </option>
                    {contactCategories.map(category => (
                      <option key={category} value={category}>
                        {intl.formatMessage({ id: `ContactPage.category.${category}` })}
                      </option>
                    ))}
                  </FieldSelect>
                  <FieldTextInput
                    id="contact-subject"
                    name="subject"
                    type="text"
                    label={intl.formatMessage({ id: 'ContactPage.subjectLabel' })}
                    required
                    maxLength={limits.subject}
                    validate={value =>
                      required(requiredMessage)(value) ||
                      maxLength(tooLong(limits.subject), limits.subject)(value)
                    }
                  />
                  <FieldTextInput
                    id="contact-message"
                    name="message"
                    type="textarea"
                    label={intl.formatMessage({ id: 'ContactPage.messageLabel' })}
                    helpText={intl.formatMessage({ id: 'ContactPage.messageHelp' })}
                    required
                    maxLength={limits.message}
                    validate={value =>
                      required(requiredMessage)(value) ||
                      maxLength(tooLong(limits.message), limits.message)(value)
                    }
                  />
                  <FieldTextInput
                    id="contact-where"
                    name="where"
                    type="text"
                    label={intl.formatMessage({ id: 'ContactPage.whereLabel' })}
                    placeholder={intl.formatMessage({ id: 'ContactPage.wherePlaceholder' })}
                    maxLength={limits.where}
                    validate={maxLength(tooLong(limits.where), limits.where)}
                  />
                  <div className={css.identityFields}>
                    <FieldTextInput
                      id="contact-name"
                      name="name"
                      type="text"
                      label={intl.formatMessage({ id: 'ContactPage.nameLabel' })}
                      disabled={!!currentName}
                      required={!currentName}
                      maxLength={limits.name}
                      validate={value =>
                        (!currentName && required(requiredMessage)(value)) ||
                        maxLength(tooLong(limits.name), limits.name)(value)
                      }
                    />
                    <FieldTextInput
                      id="contact-email"
                      name="email"
                      type="email"
                      label={intl.formatMessage({ id: 'ContactPage.emailLabel' })}
                      disabled={!!currentEmail}
                      required={!currentEmail}
                      maxLength={limits.email}
                      validate={value =>
                        (!currentEmail && required(requiredMessage)(value)) ||
                        emailFormatValid(validEmailMessage)(value) ||
                        maxLength(tooLong(limits.email), limits.email)(value)
                      }
                    />
                  </div>

                  <div className={css.status} aria-live="polite">
                    {submissionState === 'success' ? (
                      <div className={css.success} role="status">
                        <p>
                          <FormattedMessage id="ContactPage.success" />
                        </p>
                        <p>
                          <FormattedMessage id="ContactPage.successDetail" />
                        </p>
                      </div>
                    ) : submissionState === 'error' ? (
                      <p className={css.error} role="alert">
                        <FormattedMessage id="ContactPage.error" />
                      </p>
                    ) : null}
                  </div>
                  <PrimaryButton
                    className={css.submitButton}
                    type="submit"
                    disabled={invalid || submissionState === 'submitting'}
                    inProgress={submissionState === 'submitting'}
                  >
                    <FormattedMessage id="ContactPage.submit" />
                  </PrimaryButton>
                </Form>
              )}
            />
          </div>
        </main>
      </LayoutSingleColumn>
    </Page>
  );
};

export default ContactPage;
